import { useCallback, useEffect, useReducer } from 'react';
import { getUnread, markNodeSeen } from './apiLive';

type Counts = Record<string, number>;

/**
 * One store per plan, shared by every caller: the page's hook refreshes it after each
 * reload, and the document view and the canvas read and clear it without being handed
 * anything by the page.
 */
interface Entry {
  counts: Counts;
  listeners: Set<() => void>;
  /** Stamps requests and seen marks in one order. */
  seq: number;
  /** The newest request whose answer has been applied. */
  applied: number;
  /** Node id to the stamp of its last seen mark. */
  seenAt: Map<string, number>;
}

const EMPTY: Counts = {};
const entries = new Map<string, Entry>();

const entryFor = (planId: string): Entry => {
  let e = entries.get(planId);
  if (!e) {
    e = { counts: EMPTY, listeners: new Set(), seq: 0, applied: 0, seenAt: new Map() };
    entries.set(planId, e);
  }
  return e;
};

const emit = (e: Entry) => e.listeners.forEach((l) => l());

const refresh = async (planId: string): Promise<void> => {
  const e = entryFor(planId);
  const stamp = ++e.seq;
  let res: { counts: Counts } | undefined;
  try {
    res = await getUnread(planId);
  } catch {
    return; // A hint, not a fact: a failed load keeps what is shown.
  }
  if (stamp < e.applied) return;
  e.applied = stamp;
  const next: Counts = {};
  Object.entries(res?.counts ?? {}).forEach(([id, n]) => {
    // Seen after this request left: the server's answer predates the mark.
    if ((e.seenAt.get(id) ?? 0) > stamp) return;
    if (n > 0) next[id] = n;
  });
  e.counts = next;
  emit(e);
};

const clearLocal = (e: Entry, nodeId: string) => {
  e.seenAt.set(nodeId, ++e.seq);
  if (!e.counts[nodeId]) return;
  const { [nodeId]: _cleared, ...rest } = e.counts;
  e.counts = rest;
  emit(e);
};

/**
 * Mark a node seen here and on the server, whatever the local count says: for a
 * person's own writes, which the server counts before any refresh shows them.
 */
export const markNodeSeenNow = (planId: string, nodeId: string): Promise<void> => {
  const held = entries.get(planId);
  if (held) clearLocal(held, nodeId);
  return Promise.resolve()
    .then(() => markNodeSeen(planId, nodeId))
    .then(() => {
      // A refresh that left before the server took the mark would bring the count back.
      const e = entries.get(planId);
      if (e) clearLocal(e, nodeId);
    })
    .catch(() => undefined);
};

const markSeen = (planId: string, nodeId: string) => {
  const e = entryFor(planId);
  if (!e.counts[nodeId]) {
    e.seenAt.set(nodeId, ++e.seq);
    return;
  }
  markNodeSeenNow(planId, nodeId);
};

export interface Unread {
  /** Node id to how much changed there since this person last looked. Zero counts are absent. */
  counts: Counts;
  /** Clear a node's count here and on the server. Does nothing when it has none. */
  markSeen: (nodeId: string) => void;
  refresh: () => Promise<void>;
}

/**
 * Per-node unread counts for this person. `load`: fetch on mount when nothing has
 * asked yet. The page's `useNodePlan` passes false because it refreshes after every reload.
 */
export function useUnread(planId: string, load = true): Unread {
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const e = entryFor(planId);
    e.listeners.add(rerender);
    if (load && e.seq === 0) refresh(planId);
    return () => {
      e.listeners.delete(rerender);
      if (e.listeners.size === 0 && entries.get(planId) === e) entries.delete(planId);
    };
    // `load` only matters on the first subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const doRefresh = useCallback(() => refresh(planId), [planId]);
  const doMarkSeen = useCallback((nodeId: string) => markSeen(planId, nodeId), [planId]);

  return { counts: entries.get(planId)?.counts ?? EMPTY, markSeen: doMarkSeen, refresh: doRefresh };
}
